import { IMessageContext } from "@saleor/components/messages";
import useNotifier from "@saleor/hooks/useNotifier";
import ApolloClient from "apollo-client";
import React from "react";
import { useApolloClient } from "react-apollo";
import { IntlShape, useIntl } from "react-intl";

import BackgroundTasksContext from "./context";
import {
  checkExportFileStatus,
  checkOrderInvoicesStatus,
  checkShopifySyncStatus,
  checkUploadStatus,
  checkVoucherJob,
  checkVoucherJobBulk
} from "./queries";
import {
  handleTask,
  queueCustom,
  queueExport,
  queueInvoiceGenerate,
  queueShopifySync,
  queueUpdateGroupBulkVoucher,
  queueUpload,
  queueVoucherJobDetails
} from "./tasks";
import { QueuedTask, Task, TaskData, TaskStatus } from "./types";

export const backgroundTasksRefreshTime = 1 * 1000;

export function useBackgroundTasks(
  apolloClient: ApolloClient<any>,
  notify: IMessageContext,
  intl: IntlShape
) {
  const idCounter = React.useRef(0);
  const tasks = React.useRef<QueuedTask[]>([]);

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      const queue = async () => {
        try {
          await Promise.all(
            tasks.current.map(async task => {
              if (task.status === TaskStatus.PENDING) {
                let status: TaskStatus;

                try {
                  status = await handleTask(task);
                } catch (error) {
                  const taskIndex = tasks.current.findIndex(
                    t => t.id === task.id
                  );
                  tasks.current[taskIndex].status = TaskStatus.FAILURE;
                  throw error;
                }
                if (status !== TaskStatus.PENDING) {
                  const taskIndex = tasks.current.findIndex(
                    t => t.id === task.id
                  );
                  tasks.current[taskIndex].status = status;
                }
              }
            })
          );
        } catch (error) {
          throw error;
        }
      };

      queue();
    }, backgroundTasksRefreshTime);

    return () => clearInterval(intervalId);
  });

  function cancel(id: number) {
    tasks.current = tasks.current.filter(task => task.id !== id);
  }

  function queue(type: Task, data?: TaskData) {
    idCounter.current += 1;
    switch (type) {
      case Task.CUSTOM:
        queueCustom(idCounter.current, tasks, data);
        break;
      case Task.INVOICE_GENERATE:
        queueInvoiceGenerate(
          idCounter.current,
          data.generateInvoice,
          tasks,
          () =>
            apolloClient.query({
              fetchPolicy: "network-only",
              query: checkOrderInvoicesStatus,
              variables: {
                id: data.generateInvoice.orderId
              }
            }),
          notify,
          intl
        );
        break;
      case Task.EXPORT:
        queueExport(
          idCounter.current,
          tasks,
          () =>
            apolloClient.query({
              fetchPolicy: "network-only",
              query: checkExportFileStatus,
              variables: {
                id: data.id
              }
            }),
          notify,
          intl
        );
        break;
      case Task.UPLOAD:
        queueUpload(
          idCounter.current,
          tasks,
          () =>
            apolloClient.query({
              fetchPolicy: "network-only",
              query: checkUploadStatus,
              variables: {
                id: data.id
              }
            }),
          notify,
          intl,
          data.onResponseData
        );
        break;
      case Task.SHOPIFY_SYNC:
        setTimeout(
          () =>
            queueShopifySync(
              idCounter.current,
              tasks,
              () =>
                apolloClient.query({
                  fetchPolicy: "network-only",
                  query: checkShopifySyncStatus,
                  variables: {
                    id: data.id
                  }
                }),
              notify,
              intl
            ),
          2000
        );
        break;
      case Task.BULK_VOUCHER:
        queueVoucherJobDetails(
          idCounter.current,
          tasks,
          () =>
            apolloClient.query({
              fetchPolicy: "network-only",
              query: checkVoucherJob,
              variables: {
                id: data.id
              }
            }),
          notify,
          intl,
          data.onResponseData
        );
        break;
      case Task.BULK_VOUCHER_UPDATE:
        queueUpdateGroupBulkVoucher(
          idCounter.current,
          tasks,
          () =>
            apolloClient.query({
              fetchPolicy: "network-only",
              query: checkVoucherJobBulk,
              variables: {
                id: data.id
              }
            }),
          notify,
          intl,
          data.onResponseData
        );
    }
    return idCounter.current;
  }

  return {
    cancel,
    queue
  };
}

const BackgroundTasksProvider: React.FC = ({ children }) => {
  const apolloClient = useApolloClient();
  const notify = useNotifier();
  const intl = useIntl();
  const { cancel, queue } = useBackgroundTasks(apolloClient, notify, intl);

  return (
    <BackgroundTasksContext.Provider
      value={{
        cancel,
        queue
      }}
    >
      {children}
    </BackgroundTasksContext.Provider>
  );
};

BackgroundTasksProvider.displayName = "BackgroundTasksProvider";
export default BackgroundTasksProvider;
